import React from 'react';
import toast from 'react-hot-toast';
import { Column } from '../../../components/DataTable/DataTable';
import { TruncatedText } from '../../../components/DataTable/TruncatedText';
import { Link, useLocation } from 'react-router-dom';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import { accountingReturnState } from '../shared/navigation';
import Badge from '../../../components/ui/Badge';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../../config/appConfig';
import type { AccDocument } from './useData';
import { CheckCircleIcon, EyeIcon, LockIcon, PencilIcon, TrashIcon, UndoIcon, useConfirm } from '../../../components/ui';
import { revertAccDocumentToDraft, saveAccDocument } from '../../../services/mutations';
import { resolveDocumentEditRoute } from './formTemplates';

const statusVariant = (status: string) => {
  if (status === 'DRAFT') return 'default';
  if (status === 'TEMP') return 'warning';
  return 'success';
};

const formatJournalStatus = (status: string) => {
  if (status === 'DRAFT') return 'Draft';
  if (status === 'TEMP') return 'Temp';
  if (status === 'SAVED') return 'Saved';
  return status;
};

const formatAmount = (value?: number | null) => (value != null ? value.toFixed(2) : '0.00');

const formatUserLabel = (user?: AccDocument['createdByUser']) => {
  if (!user) return '—';
  const display = user.userDisplayName?.trim();
  if (display) return display;
  const fullName = `${user.userFirstName ?? ''} ${user.userLastName ?? ''}`.trim();
  return fullName || user.userEmail || '—';
};

const formatCompanyCodes = (row?: AccDocument) => {
  if (row?.companyCodes?.length) return row.companyCodes.join(', ');
  const fromLines = row?.lines
    ?.map((line) => line.company?.code ?? line.company?.name)
    .filter((value): value is string => !!value);
  if (!fromLines?.length) return '—';
  return Array.from(new Set(fromLines)).join(', ');
};

const isJournalBalanced = (row?: AccDocument) => {
  if (row?.lineTotals?.isBalanced != null) return row.lineTotals.isBalanced;
  const totals = row?.lineTotals;
  if (!totals) return true;
  return Math.abs(totals.debitUSD - totals.creditUSD) < 0.005
    && Math.abs(totals.debitLocal - totals.creditLocal) < 0.005;
};

export const buildYearFilterOptions = (minYear: number, maxYear: number) => {
  const from = Math.min(minYear, maxYear);
  const to = Math.max(minYear, maxYear);
  const options: Array<{ value: string; label: string }> = [];
  for (let year = to; year >= from; year -= 1) {
    options.push({ value: String(year), label: String(year) });
  }
  return options;
};

const journalYearFromItem = (item: AccDocument) => {
  const dateValue = item.postingDate ?? item.documentDate;
  if (!dateValue) return '';
  const parsed = new Date(dateValue);
  return Number.isNaN(parsed.getTime()) ? '' : String(parsed.getFullYear());
};

export const moduleConfig = {
  module: {
    title: 'Journals',
    permissionModuleKey: 'acc_documents',
    route: {
      list: '/accounting/journals',
      new: '/accounting/journals/new',
      detail: (id: number) => `/accounting/journals/${id}`,
      edit: (id: number) => `/accounting/journals/${id}/edit`},
    api: { list: '/acc-documents', detail: (id: number) => `/acc-documents/${id}` }},
  pagination: { initialItemsPerPage: APP_CONFIG.table.defaultPageSize, itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions },
  search: { placeholder: 'Search journals…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['year', 'status']},
  ],
  filters: {
    fields: [
      {
        key: 'year',
        label: 'Year',
        type: 'select' as const,
        placeholder: 'All years',
        getValue: journalYearFromItem,
        options: []},
      {
        key: 'status',
        label: 'Status',
        type: 'select' as const,
        placeholder: 'All statuses',
        getValue: (item: AccDocument) => item.status,
        options: [
          { value: 'DRAFT', label: 'Draft' },
          { value: 'TEMP', label: 'Temp (import issues)' },
          { value: 'SAVED', label: 'Saved' },
        ]},
    ],
    applyFilter: (items: AccDocument[]) => items,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New journal', showTitle: false, variant: 'primary' as const, href: '/accounting/journals/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccDocument) => item.id,
  },
  emptyStates: { noData: 'Create a journal entry.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete journal',
    message: 'Delete this draft journal?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deleteAccDocument } = await import('../../../services/mutations');
      await deleteAccDocument(id);
    }},
  utils: { formatDate: (d?: string) => (d ? formatDateGlobal(d) : '') },
  detail: {
    subtitle: 'Journal entry',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Document not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Journal',
    getTitle: (item: AccDocument) => item.code || `Draft #${item.id}`},
  fields: []};

export const getColumns = (formatDate: (d?: string) => string): Column<AccDocument>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v, row) => v || `Draft #${row?.id}` },
  { key: 'postingDate', header: 'Posting date', sortable: true, render: (v) => formatDate(v) },
  { key: 'documentType', header: 'Type', render: (_v, row) => row?.documentType?.name ?? row?.documentType?.code ?? row?.documentTypeId },
  { key: 'status', header: 'Status', render: (v, row) => (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={statusVariant(String(v))}>{formatJournalStatus(String(v))}</Badge>
      {row?.periodLocked && (
        <span title="Accounting period is locked" className="inline-flex text-amber-600">
          <LockIcon className="w-4 h-4" />
        </span>
      )}
    </span>
  ) },
  {
    key: 'description',
    header: 'Description',
    maxWidth: '20rem',
    render: (v) => (
      <TruncatedText text={String(v ?? '').trim()} maxLength={80} className="text-sm block" />
    )},
  {
    key: 'lineCount',
    header: 'Lines',
    render: (_v, row) => row?._count?.lines ?? row?.lines?.length ?? 0},
  {
    key: 'debitUSD',
    header: 'Debit USD',
    align: 'right',
    render: (_v, row) => formatAmount(row?.lineTotals?.debitUSD)},
  {
    key: 'creditUSD',
    header: 'Credit USD',
    align: 'right',
    render: (_v, row) => formatAmount(row?.lineTotals?.creditUSD)},
  {
    key: 'companyCodes',
    header: 'Companies',
    render: (_v, row) => formatCompanyCodes(row)},
  {
    key: 'balanced',
    header: 'Balanced',
    render: (_v, row) => (
      <Badge variant={isJournalBalanced(row) ? 'success' : 'danger'}>
        {isJournalBalanced(row) ? 'Yes' : 'No'}
      </Badge>
    )},
  {
    key: 'createdByUser',
    header: 'Created by',
    render: (_v, row) => formatUserLabel(row?.createdByUser)},
  {
    key: 'updatedByUser',
    header: 'Updated by',
    render: (_v, row) => formatUserLabel(row?.updatedByUser)},
];

export const getDetailSections = (item: AccDocument, formatDate: (s?: string) => string) => [
  {
    title: 'Header',
    fields: [
      { label: 'Code', value: item.code || '—' },
      { label: 'Type', value: item.documentType?.name ?? item.documentType?.code ?? String(item.documentTypeId) },
      { label: 'Status', value: item.status },
      { label: 'Document date', value: formatDate(item.documentDate ?? undefined) },
      { label: 'Posting date', value: formatDate(item.postingDate ?? undefined) },
      { label: 'Description', value: item.description || '—' },
      { label: 'Lines', value: item._count?.lines ?? item.lines?.length ?? 0 },
      { label: 'Debit USD', value: formatAmount(item.lineTotals?.debitUSD) },
      { label: 'Credit USD', value: formatAmount(item.lineTotals?.creditUSD) },
      { label: 'Companies', value: formatCompanyCodes(item) },
      { label: 'Balanced', value: isJournalBalanced(item) ? 'Yes' : 'No' },
      { label: 'Created by', value: formatUserLabel(item.createdByUser) },
      { label: 'Updated by', value: formatUserLabel(item.updatedByUser) },
    ]},
];

const DocumentRowActions: React.FC<{
  item: AccDocument;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const location = useLocation();
  const confirm = useConfirm();
  const returnState = accountingReturnState(location.pathname, location.search);
  const canSave = item.status === 'DRAFT' || item.status === 'TEMP';
  const canRevertToDraft = item.status === 'SAVED';

  const runStatusAction = async (action: 'save' | 'revert') => {
    const label = item.code || `Draft #${item.id}`;
    const ok = await confirm(
      action === 'save'
        ? {
            title: 'Save journal',
            message: `Save “${label}”? Saved journals can no longer be edited until reverted to draft.`,
            confirmText: 'Save',
            cancelText: 'Cancel'}
        : {
            title: 'Revert to draft',
            message: `Revert “${label}” to draft? You will be able to edit it again.`,
            confirmText: 'Revert',
            cancelText: 'Cancel',
            confirmVariant: 'danger'},
    );
    if (!ok) return;
    try {
      if (action === 'save') await saveAccDocument(item.id);
      if (action === 'revert') await revertAccDocumentToDraft(item.id);
      toast.success(action === 'save' ? 'Journal saved' : 'Journal reverted to draft');
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Action failed');
    }
  };

  return (
    <div className="flex items-center flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.id)}
        state={returnState}
        className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
        aria-label="View"
      >
        <EyeIcon className="w-5 h-5" />
      </Link>

      {canSave && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            runStatusAction('save');
          }}
          className="p-1 text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 rounded-lg transition-colors"
          title="Save"
          aria-label="Save"
        >
          <CheckCircleIcon className="w-5 h-5" />
        </button>
      )}
      {canRevertToDraft && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            runStatusAction('revert');
          }}
          className="p-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          title="Revert to draft"
          aria-label="Revert to draft"
        >
          <UndoIcon className="w-5 h-5" />
        </button>
      )}

      {canEdit && (item.status === 'DRAFT' || item.status === 'TEMP') && (
        <Link
          to={resolveDocumentEditRoute(item.documentType, item.id, moduleConfig.module.route.edit(item.id))}
          state={returnState}
          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
          aria-label="Edit"
        >
          <PencilIcon className="w-5 h-5" />
        </Link>
      )}

      {canDelete && (item.status === 'DRAFT' || item.status === 'TEMP') && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
          aria-label="Delete"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export const getTableActions = (
  item: AccDocument,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  actionOptions?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <DocumentRowActions
    item={item}
    onDelete={onDelete}
    canDelete={actionOptions?.canDelete !== false}
    canEdit={actionOptions?.canEdit !== false}
  />
);

export const getToolbarIcon = getAccountingToolbarIcon;
export const getStatusBadges = () => null;
